import { useEffect, useState } from 'preact/hooks';
import { api, ApiError } from './api';

export type Carga<T> = {
  /** Último dado recebido. Continua visível enquanto uma nova consulta carrega (evita a tela piscar). */
  dados: T | null;
  carregando: boolean;
  erro: ApiError | null;
};

/**
 * Busca `caminho` na API. Passar `null` desliga a consulta (ex.: camada que ainda não tem filtro obrigatório).
 * Troca de caminho cancela a requisição anterior; só o resultado do caminho atual é aplicado.
 * Incrementar `recarga` refaz a consulta (ex.: depois de gravar algo).
 */
export function useApi<T>(caminho: string | null, recarga = 0): Carga<T> {
  const [estado, setEstado] = useState<Carga<T>>({ dados: null, carregando: caminho !== null, erro: null });

  useEffect(() => {
    if (caminho === null) {
      setEstado({ dados: null, carregando: false, erro: null });
      return;
    }
    const ctl = new AbortController();
    setEstado((s) => ({ ...s, carregando: true, erro: null }));
    api<T>(caminho, { signal: ctl.signal })
      .then((dados) => {
        if (!ctl.signal.aborted) setEstado({ dados, carregando: false, erro: null }); // resposta velha nunca sobrescreve a nova
      })
      .catch((e: unknown) => {
        if (ctl.signal.aborted) return;
        setEstado((s) => ({ dados: s.dados, carregando: false, erro: e instanceof ApiError ? e : new ApiError(0, 'Sem conexão com o servidor') }));
      });
    return () => ctl.abort();
  }, [caminho, recarga]);

  return estado;
}

/** Atrasa a propagação de um valor que muda rápido (ex.: arrastar o controle de tempo). */
export function useDebounce<T>(valor: T, ms: number): T {
  const [v, setV] = useState(valor);
  useEffect(() => {
    const t = setTimeout(() => setV(valor), ms);
    return () => clearTimeout(t);
  }, [valor, ms]);
  return v;
}

let promessaGeo: Promise<GeoJSON.FeatureCollection<GeoJSON.Polygon, { codIbge: string; nome: string; mesorregiao: string }>> | null = null;

/** Malha dos 223 municípios, baixada uma única vez por sessão. */
export function carregarGeo() {
  promessaGeo ??= fetch('/geo/pb-municipios.json').then((r) => {
    if (!r.ok) {
      promessaGeo = null;
      throw new Error('Não foi possível carregar o mapa');
    }
    return r.json();
  });
  return promessaGeo;
}
